import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { PrizeRank, UserBuyInLog } from "../types";
import { collection, getDocs, onSnapshot, query } from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch } from ".";
import { sum } from "lodash";

interface UserBuyInLogState {
  records: UserBuyInLog[];
}

const initialState: UserBuyInLogState = {
  records: [],
};

const InitialReward = [1_000_000, 500_000, 250_000, 150_000, 100_000];

// Async Thunk to Fetch userBuyInLogs  from Firestore
export const fetchUserBuyInLogs = createAsyncThunk(
  "userBuyInLogSlice/fetchUserBuyInLogs",
  async (_, { rejectWithValue }) => {
    try {
      const userBuyInLogsRef = collection(db, "userBuyInLogs");
      const q = query(userBuyInLogsRef);
      const querySnapshot = await getDocs(q);
      const players: UserBuyInLog[] = [];
      querySnapshot.forEach((doc) => {
        players.push({ id: doc.id, ...doc.data() } as UserBuyInLog);
      });

      return players;
    } catch (error) {
      return rejectWithValue("Failed to fetch players in group");
    }
  }
);

export const subscribeToUserBuyInLogs = () => (dispatch: AppDispatch) => {
  const userBuyInLogsRef = collection(db, "userBuyInLogs");
  const q = query(userBuyInLogsRef);

  // Subscribe to real-time changes
  const unsubscribe = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      const record = {
        id: change.doc.id,
        ...change.doc.data(),
      } as UserBuyInLog;

      if (change.type === "added") {
        dispatch(addRecord(record));
      } else if (change.type === "modified") {
        dispatch(updateRecord(record));
      } else if (change.type === "removed") {
        if (record.id) {
          dispatch(removeRecord(record.id));
        }
      }
    });
  });

  return unsubscribe; // Return the unsubscribe function
};

const userBuyInLogSlice = createSlice({
  name: "userBuyInLogs",
  initialState,
  selectors: {
    getPrizeRank: (state) => {
      const totalReward =
        state.records.reduce((acc, record) => acc + record.buyInPrice, 0);

      const prizeRank: PrizeRank[] = [];
      prizeRank.push({ id: 1, name: "1st Place", prize: InitialReward[0] + totalReward * 0.5 });
      prizeRank.push({ id: 2, name: "2nd Place", prize: InitialReward[1] + totalReward * 0.2 });
      prizeRank.push({ id: 3, name: "3rd Place", prize: InitialReward[2] + totalReward * 0.15 });
      prizeRank.push({ id: 4, name: "4th Place", prize: InitialReward[3] + totalReward * 0.1 });
      prizeRank.push({ id: 5, name: "5th Place", prize: InitialReward[4] + totalReward * 0.05 });

      return { prizeRank, totalReward: totalReward + sum(InitialReward) };
    },
  },
  reducers: {
    clearState: (state) => {
      state.records = [];
    },
    setRecords(state, action: PayloadAction<UserBuyInLog[]>) {
      state.records = action.payload;
    },
    addRecord(state, action: PayloadAction<UserBuyInLog>) {
      state.records.push(action.payload);
    },
    updateRecord(state, action: PayloadAction<UserBuyInLog>) {
      const index = state.records.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.records[index] = action.payload;
      }
    },
    removeRecord(state, action: PayloadAction<string>) {
      state.records = state.records.filter((p) => p.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserBuyInLogs.fulfilled, (state, action) => {
      state.records = action.payload;
    });
  },
});

export const { clearState, addRecord, updateRecord, setRecords, removeRecord } =
  userBuyInLogSlice.actions;

export default userBuyInLogSlice.reducer;

export const {
  getPrizeRank,
} = userBuyInLogSlice.selectors;
